import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Spinner, Button } from 'flowbite-react';
import { useGetSupportQuestions } from 'src/hooks/support.hooks';
import { SupportQuestionCategory } from 'types';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import SupportModal from './SupportModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { HiChevronRight, HiChevronDown } from 'react-icons/hi';
import { useUserContext } from 'src/context/UserProvider';

interface SupportQuestionsSectionProps {
	onOpenCreateTicket?: () => void;
}

const SupportQuestionsSection: React.FC<SupportQuestionsSectionProps> = ({ onOpenCreateTicket }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

	// URL as source of truth for modal: ?category=X&subcategory=Y&batchId=Z
	const categoryParam = searchParams.get('category');
	const subcategoryParam = searchParams.get('subcategory');
	const batchIdParam = searchParams.get('batchId');

	const showSupportModal = !!(categoryParam && subcategoryParam);
	const supportModalPrefill = useMemo(() => {
		if (!categoryParam || !subcategoryParam) return undefined;
		const batchId = batchIdParam !== null && batchIdParam !== ''
			? Number(batchIdParam)
			: (selectedBatchId ?? 0);
		const resolvedBatchId = Number.isNaN(batchId) ? 0 : batchId;
		// batchId=0 (Placement) means company-related questions; other batches are general
		const questionType = resolvedBatchId === 0 ? SupportQuestionCategory.COMPANY : SupportQuestionCategory.GENERAL;
		return {
			category: categoryParam,
			subcategory: subcategoryParam,
			questionType,
			batchId: resolvedBatchId,
		};
	}, [categoryParam, subcategoryParam, batchIdParam, selectedBatchId]);

	// Get user's batch details from lmsBatchDetails
	// Include batch 0 (Placement) which is common for all users
	const userBatches = useMemo(() => {
		const batches: Array<{ id: number; name: string; isPlacementDrive?: boolean }> = [
			{ id: 0, name: 'Placement', isPlacementDrive: true }, // Common for all users
		];

		if (user?.lmsBatchDetails && Array.isArray(user.lmsBatchDetails)) {
			user.lmsBatchDetails.forEach((bd: any) => {
				// Don't add batch 0 again if it exists in lmsBatchDetails
				if (bd.batchId !== 0) {
					batches.push({
						id: bd.batchId,
						name: bd.batchName,
						isPlacementDrive: false,
					});
				}
			});
		}

		return batches;
	}, [user?.lmsBatchDetails]);

	// Fetch dropdown menu items for categories and subcategories (use public route for jobseekers)
	const { data: categoriesData, isLoading: isLoadingCategories } = useGetDropdownItemsByCategory('SUPPORT_TICKET_CATEGORY', undefined, true, true);
	const { data: subcategoriesData, isLoading: isLoadingSubcategories } = useGetDropdownItemsByCategory('SUPPORT_TICKET_SUB_CATEGORY', undefined, true, true);

	const categories = useMemo(() => categoriesData?.lmsTechStacks || [], [categoriesData]);
	const subcategories = useMemo(() => subcategoriesData?.lmsTechStacks || [], [subcategoriesData]);

	// Fetch questions based on selected batchId only
	// Don't filter by category/questionType here - we'll filter categories based on which questions exist
	const { data: questionsData, isLoading: isLoadingQuestions } = useGetSupportQuestions(
		undefined, // Don't filter by category/questionType
		undefined,
		undefined,
		selectedBatchId !== null ? selectedBatchId : undefined, // Pass batchId (including 0)
	);

	// Sync selectedBatchId from URL when modal is open via URL (e.g. direct link or refresh)
	useEffect(() => {
		if (batchIdParam !== null && batchIdParam !== '') {
			const num = Number(batchIdParam);
			if (!Number.isNaN(num)) setSelectedBatchId(num);
		}
	}, [batchIdParam]);

	// Filter questions by batchId (must be called before any conditional returns)
	const filteredQuestions = useMemo(() => {
		const data = questionsData?.data ?? [];
		if (!data.length || selectedBatchId === null) return [];

		// For batch 0 (Company), show all questions with batchId = 0 or undefined/null
		if (selectedBatchId === 0) {
			return data.filter((q: any) => {
				return q.batchId === 0 || q.batchId === undefined || q.batchId === null;
			});
		}

		// For other batches (General), filter by selectedBatchId
		return data.filter((q: any) => {
			return q.batchId === selectedBatchId;
		});
	}, [questionsData, selectedBatchId]);


	// Update categoriesWithQuestions based on filtered questions
	// Filter categories by question type based on selected batch
	// Questions store supportCategory = subcategory's name (value)
	// Subcategories have supportCategory = parent category's name (value)
	// So we need to find subcategories that have questions, then get their parent categories
	const filteredCategoriesWithQuestions = useMemo(() => {
		if (!filteredQuestions.length || selectedBatchId === null) return [];

		// If categories or subcategories haven't loaded yet, return empty array (they're still loading)
		if (!categories.length || !subcategories.length) return [];

		// Get all unique subcategory names from questions (these are stored in questions.supportCategory)
		const questionSubcategoryNames = new Set(filteredQuestions.map((q: any) => q.supportCategory).filter(Boolean));

		// Find subcategories that have questions
		const subcategoriesWithQuestions = subcategories.filter((sub: any) =>
			questionSubcategoryNames.has(sub.name) && sub.isActive
		);

		// If no subcategories match, it means the questions reference subcategories that don't exist in dropdown menu
		// This shouldn't happen, but we'll handle it gracefully
		if (subcategoriesWithQuestions.length === 0) {
			console.warn('No subcategories found for questions:', Array.from(questionSubcategoryNames));
			return [];
		}

		// Get parent category IDs and names from subcategories that have questions
		// Support both ID-based (new) and name-based (legacy) matching
		const parentCategoryIds = new Set(
			subcategoriesWithQuestions
				.map((sub: any) => {
					// Check if supportCategoryId is populated (new way) or use supportCategory (legacy)
					if (sub.supportCategoryId) {
						return typeof sub.supportCategoryId === 'object'
							? sub.supportCategoryId._id?.toString()
							: sub.supportCategoryId.toString();
					}
					return null;
				})
				.filter(Boolean)
		);
		const parentCategoryNames = new Set(
			subcategoriesWithQuestions.map((sub: any) => sub.supportCategory).filter(Boolean)
		);

		// Placement (batchId=0) is common for all users: show any category that has subcategories with questions.
		// Other batches: only show categories with questionType = GENERAL.
		const isPlacement = selectedBatchId === 0;
		const expectedQuestionType = SupportQuestionCategory.GENERAL;

		return categories.filter((cat: any) => {
			const categoryId = cat._id?.toString();
			const categoryValue = cat.name;
			const categoryQuestionType = cat.questionType || SupportQuestionCategory.GENERAL;

			const matchesById = parentCategoryIds.has(categoryId);
			const matchesByName = parentCategoryNames.has(categoryValue);
			const hasSubcategoriesWithQuestions = matchesById || matchesByName;

			// For Placement, include category if it has subcategories with questions (ignore questionType)
			if (isPlacement) {
				return hasSubcategoriesWithQuestions && cat.isActive;
			}
			// For other batches, require GENERAL
			return categoryQuestionType === expectedQuestionType && hasSubcategoriesWithQuestions && cat.isActive;
		});
	}, [filteredQuestions, categories, subcategories, selectedBatchId]);

	// Batches that have at least one category with questions (so we show search vs fallback modal)
	const batchIdsWithQuestions = useMemo(() => {
		const set = new Set<number>();
		const data = questionsData?.data ?? [];
		if (!data.length || !categories.length || !subcategories.length) return set;
		userBatches.forEach((b) => {
			const batchId = b.id;
			const qs =
				batchId === 0
					? data.filter((q: any) => q.batchId === 0 || q.batchId == null)
					: data.filter((q: any) => q.batchId === batchId);
			if (qs.length === 0) return;
			const questionSubcategoryNames = new Set(qs.map((q: any) => q.supportCategory).filter(Boolean));
			const subcategoriesWithQuestions = subcategories.filter(
				(sub: any) => questionSubcategoryNames.has(sub.name) && sub.isActive
			);
			if (subcategoriesWithQuestions.length === 0) return;
			const parentCategoryIds = new Set(
				subcategoriesWithQuestions
					.map((sub: any) => {
						if (sub.supportCategoryId) {
							return typeof sub.supportCategoryId === 'object'
								? sub.supportCategoryId._id?.toString()
								: sub.supportCategoryId.toString();
						}
						return null;
					})
					.filter(Boolean)
			);
			const parentCategoryNames = new Set(
				subcategoriesWithQuestions.map((sub: any) => sub.supportCategory).filter(Boolean)
			);
			const isPlacement = batchId === 0;
			const hasCategory = categories.some((cat: any) => {
				const categoryId = cat._id?.toString();
				const categoryValue = cat.name;
				const categoryQuestionType = cat.questionType || SupportQuestionCategory.GENERAL;
				const matchesById = parentCategoryIds.has(categoryId);
				const matchesByName = parentCategoryNames.has(categoryValue);
				const hasSub = matchesById || matchesByName;
				if (isPlacement) return hasSub && cat.isActive;
				return categoryQuestionType === SupportQuestionCategory.GENERAL && hasSub && cat.isActive;
			});
			if (hasCategory) set.add(batchId);
		});
		return set;
	}, [questionsData?.data, categories, subcategories, userBatches]);

	// Check if subcategory has questions
	const subcategoryHasQuestions = useCallback((subcategoryItem: any) => {
		if (!filteredQuestions.length) return false;
		const subcategoryValue = subcategoryItem.name;
		return filteredQuestions.some((q: any) => q.supportCategory === subcategoryValue);
	}, [filteredQuestions]);

	// Get all subcategories for all categories (for sidebar display)
	// MUST be called before any conditional returns to follow Rules of Hooks
	const allSubcategoriesWithCategory = useMemo(() => {
		if (!filteredCategoriesWithQuestions.length) return [];

		const result: Array<{ category: any; subcategories: any[] }> = [];

		filteredCategoriesWithQuestions.forEach((category: any) => {
			const categoryId = category._id?.toString();
			const categoryValue = category.name;

			// Find subcategories that belong to this category
			const categorySubcategories = subcategories.filter((sub: any) => {
				if (!sub.isActive) return false;

				// Check by ID first (new way)
				if (sub.supportCategoryId) {
					const subParentId = typeof sub.supportCategoryId === 'object'
						? sub.supportCategoryId._id?.toString()
						: sub.supportCategoryId.toString();
					if (subParentId === categoryId) return true;
				}

				// Fallback to name-based matching (legacy)
				const parentCategoryValue = sub.supportCategory;
				return parentCategoryValue === categoryValue;
			}).filter((sub: any) => {
				// Only include subcategories that have questions
				return subcategoryHasQuestions(sub);
			});

			if (categorySubcategories.length > 0) {
				result.push({ category, subcategories: categorySubcategories });
			}
		});

		return result;
	}, [filteredCategoriesWithQuestions, subcategories, subcategoryHasQuestions]);

	const [selectedCategoryForSubcategories, setSelectedCategoryForSubcategories] = useState<string | null>(null);
	// Mobile accordion: which category is expanded (one at a time)
	const [expandedCategoryName, setExpandedCategoryName] = useState<string | null>(null);

	// Initialize selected category when categories are loaded (desktop sidebar only; mobile accordion stays closed by default)
	useEffect(() => {
		if (allSubcategoriesWithCategory.length > 0 && !selectedCategoryForSubcategories) {
			setSelectedCategoryForSubcategories(allSubcategoriesWithCategory[0].category.name);
		}
	}, [allSubcategoriesWithCategory, selectedCategoryForSubcategories]);

	// Get subcategories for selected category (must be before conditional returns)
	const selectedCategoryData = useMemo(() => {
		return allSubcategoriesWithCategory.find(
			(item) => item.category.name === selectedCategoryForSubcategories
		);
	}, [allSubcategoriesWithCategory, selectedCategoryForSubcategories]);

	const handleBatchSelect = (batchId: number) => {
		setSelectedBatchId(batchId);
		const next = new URLSearchParams(searchParams);
		next.set('batchId', String(batchId));
		// When batch has no questions/categories, open fallback with default category/subcategory
		if (batchIdsWithQuestions.has(batchId)) {
			next.set('modal', 'search');
			next.delete('category');
			next.delete('subcategory');
		} else {
			next.set('modal', 'fallback');
			next.set('category', 'fallback-no-poc-mapped');
			next.set('subcategory', 'General');
		}
		const qs = next.toString();
		navigate(qs ? `/support?${qs}` : '/support', { replace: true });
	};

	const handleSubcategoryClick = (categoryName: string, subcategoryName: string) => {
		const batchId = selectedBatchId ?? 0;
		const next = new URLSearchParams(searchParams);
		next.set('category', categoryName);
		next.set('subcategory', subcategoryName);
		next.set('batchId', String(batchId));
		setSearchParams(next, { replace: true });
	};

	const handleSupportModalClose = () => {
		// Clear support-related params (including ticket-create step and ticket details) so URL is source of truth
		const next = new URLSearchParams(searchParams);
		next.delete('category');
		next.delete('subcategory');
		next.delete('batchId');
		next.delete('step');
		next.delete('questionId');
		next.delete('ticketId');
		const qs = next.toString();
		navigate(qs ? `/support?${qs}` : '/support', { replace: true });
	};

	// Step 1: Batch Selection (from user's lmsBatchDetails + batch 0)
	if (selectedBatchId === null) {
		if (userBatches.length === 0) {
			return (
				<Card className="bg-white dark:bg-[#1F2A37]">
					<h2 className="text-xl font-semibold mb-6 dark:text-white">No Batches Found</h2>
					<p className="text-gray-600 dark:text-gray-400">
						You don't have any batch details assigned. Please contact support for assistance.
					</p>
				</Card>
			);
		}

		return (
			<Card className="bg-white dark:bg-[#1F2A37]">
				<h2 className="text-xl font-semibold mb-6 dark:text-white">Select Batch</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
					{userBatches.map((batch: any) => (
						<button
							key={batch.id}
							onClick={() => handleBatchSelect(batch.id)}
							className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-500 transition-colors text-left"
						>
							{!batch.isPlacementDrive && (
								<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Batch</div>
							)}
							<div className="font-semibold dark:text-white">{batch.name}</div>
						</button>
					))}
				</div>
			</Card>
		);
	}

	// Step 2: Categories and Subcategories (only show if questions exist)
	if (selectedBatchId !== null) {
		// Show loading state if questions or categories are loading
		if (isLoadingQuestions || isLoadingCategories || isLoadingSubcategories) {
			return (
				<Card className="bg-white dark:bg-[#1F2A37]">
					<div className="flex items-center gap-4 mb-6">
						<button
							onClick={() => {
								setSelectedBatchId(null);
							}}
							className="text-purple-600 dark:text-purple-400 hover:underline"
						>
							← Back
						</button>
						<h2 className="text-xl font-semibold dark:text-white">Loading...</h2>
					</div>
					<div className="flex justify-center py-8">
						<Spinner size="xl" />
					</div>
				</Card>
			);
		}

		// Show "No Questions Found" only if we have no questions OR if we have questions but no matching categories
		if (filteredCategoriesWithQuestions.length === 0) {
			const hasQuestionsButNoCategories = filteredQuestions.length > 0;

			return (
				<Card className="bg-white dark:bg-[#1F2A37]">
					<div className="flex items-center gap-4 mb-6">
						<button
							onClick={() => {
								setSelectedBatchId(null);
							}}
							className="text-purple-600 dark:text-purple-400 hover:underline"
						>
							← Back
						</button>
						<h2 className="text-xl font-semibold dark:text-white">No Questions Found</h2>
					</div>
					{hasQuestionsButNoCategories ? (
						<>
							<p className="text-gray-600 dark:text-gray-400">
								Questions exist but no matching categories found. Please ensure each question’s subcategory exists in the dropdown menu and is linked to a parent category.
							</p>
							<div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
								<p className="text-sm text-yellow-800 dark:text-yellow-200">
									<strong>Debug Info:</strong> Found {filteredQuestions.length} question(s). Question supportCategory values: {Array.from(new Set(filteredQuestions.map((q: any) => q.supportCategory).filter(Boolean))).join(', ')}
								</p>
								<p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2">
									Available subcategories: {subcategories.length > 0 ? subcategories.map((s: any) => s.name).join(', ') : 'None loaded'}
								</p>
							</div>
						</>
					) : (
						<div className="flex flex-wrap items-center gap-3">
							<p className="text-gray-600 dark:text-gray-400">
								Have doubts about your program? Click on{' '}
								<button
									type="button"
									onClick={() => {
										const next = new URLSearchParams(searchParams);
										next.set('modal', 'fallback');
										next.set('category', 'fallback-no-poc-mapped');
										next.set('subcategory', 'General');
										if (selectedBatchId !== null) next.set('batchId', String(selectedBatchId));
										navigate(`/support?${next.toString()}`, { replace: true });
									}}
									className="text-purple-600 dark:text-purple-400 font-medium hover:underline focus:outline-none"
								>
									Create Ticket
								</button>
								{' '}and our support team will assist you shortly.
							</p>
							<Button
								color="purplePlaced"
								onClick={() => {
									const next = new URLSearchParams(searchParams);
									next.set('modal', 'fallback');
									next.set('category', 'fallback-no-poc-mapped');
									next.set('subcategory', 'General');
									if (selectedBatchId !== null) next.set('batchId', String(selectedBatchId));
									navigate(`/support?${next.toString()}`, { replace: true });
								}}
								className="flex-shrink-0"
							>
								Create Ticket
							</Button>
						</div>
					)}
				</Card>
			);
		}

		return (
			<>
				<Card className="bg-white dark:bg-[#1F2A37]">
					<div className="flex items-center gap-4 mb-6">
						<button
							onClick={() => {
								setSelectedBatchId(null);
							}}
							className="text-purple-600 dark:text-purple-400 hover:underline"
						>
							← Back
						</button>
						<h2 className="text-xl font-semibold dark:text-white">Categories</h2>
					</div>
					{isLoadingQuestions ? (
						<div className="flex justify-center py-8">
							<Spinner size="xl" />
						</div>
					) : (
						<>
							{/* Mobile: accordion list, scrollable */}
							<div className="md:hidden overflow-y-auto max-h-[calc(100vh-220px)] min-h-0 pb-4">
								<div className="space-y-1">
									{allSubcategoriesWithCategory.map((item: any) => {
										const categoryLabel = item.category.label || item.category.name;
										const isExpanded = expandedCategoryName === item.category.name;

										return (
											<div
												key={item.category._id || item.category.name}
												className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
											>
												<button
													type="button"
													onClick={() => setExpandedCategoryName(isExpanded ? null : item.category.name)}
													className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${isExpanded
														? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 font-medium'
														: 'bg-white dark:bg-[#1F2A37] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
													}`}
												>
													<span>{categoryLabel}</span>
													{isExpanded ? (
														<HiChevronDown className="w-5 h-5 flex-shrink-0" />
													) : (
														<HiChevronRight className="w-5 h-5 flex-shrink-0" />
													)}
												</button>
												{isExpanded && item.subcategories.length > 0 && (
													<div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
														{item.subcategories.map((subcategory: any) => {
															const subcategoryValue = subcategory.name;
															const subcategoryLabel = subcategory.label || subcategoryValue;
															return (
																<button
																	key={subcategory._id || subcategoryValue}
																	type="button"
																	onClick={() => handleSubcategoryClick(item.category.name, subcategoryValue)}
																	className="w-full text-left pl-6 pr-4 py-3 text-sm text-purple-800 dark:text-purple-200 hover:bg-purple-50 dark:hover:bg-purple-900/30 flex items-center justify-between"
																>
																	<span>{subcategoryLabel}</span>
																	<HiChevronRight className="w-4 h-4" />
																</button>
															);
														})}
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>

							{/* Desktop: sidebar + subcategories */}
							<div className="hidden md:flex gap-6 min-h-0">
								{/* Categories Sidebar */}
								<div className="w-64 flex-shrink-0">
									<div className="space-y-1">
										{allSubcategoriesWithCategory.map((item: any) => {
											const categoryLabel = item.category.label || item.category.name;
											const isSelected = selectedCategoryForSubcategories === item.category.name;

											return (
												<button
													key={item.category._id || item.category.name}
													onClick={() => setSelectedCategoryForSubcategories(item.category.name)}
													className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${isSelected
														? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 font-medium'
														: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
														}`}
												>
													{categoryLabel}
												</button>
											);
										})}
									</div>
								</div>

								{/* Subcategories Content */}
								<div className="flex-1 min-w-0">
									{selectedCategoryData && selectedCategoryData.subcategories.length > 0 ? (
										<div className="space-y-2">
											{selectedCategoryData.subcategories.map((subcategory: any) => {
												const subcategoryValue = subcategory.name;
												const subcategoryLabel = subcategory.label || subcategoryValue;

												return (
													<button
														key={subcategory._id || subcategoryValue}
														onClick={() => handleSubcategoryClick(selectedCategoryData.category.name, subcategoryValue)}
														className="w-full text-left px-4 py-3 bg-purple-50 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900 transition-colors flex items-center justify-between"
													>
														<span>{subcategoryLabel}</span>
														<HiChevronRight className="w-4 h-4" />
													</button>
												);
											})}
										</div>
									) : (
										<div className="text-gray-600 dark:text-gray-400 py-8 text-center">
											No subcategories available for this category.
										</div>
									)}
								</div>
							</div>
						</>
					)}
				</Card>

				{showSupportModal && supportModalPrefill && (
					<SupportModal
						show={showSupportModal}
						onClose={handleSupportModalClose}
						prefillData={supportModalPrefill}
					/>
				)}
			</>
		);
	}

	return null;
};

export default SupportQuestionsSection;
